import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import queryString from 'query-string';
import cn from 'classnames';
import { Box, Grid } from '@material-ui/core';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { goToContractStep } from '../../helpers/routeHelper';
import useDeviceResize from '../../hooks/useDeviceResize';
import { useClearAllNotifications } from '../../hooks/useClearAllNotifications';
import contractSteps from './contractSteps';
import EfesContentHeader from '../../components/EfesContentHeader';
import { ROADCARE_CONTRACT_SIGN_PATH } from '../../constants/routes';
import WizardWrapper from '../../components/WizardWrapper';
import { getUnlockedStepsUpToPayment, handleContractSingerAction } from '../../helpers/contractHelpers';
import { ACTION_NAME, APPLICATION_CONTRACT_TYPES } from '../../constants';
import Loader from '../../components/Loaders/Loader';
import { EFES_CONTENT_WIDTH, FULL_WIDTH } from '../../constants/uiConstants';
import {
  clearRoadCareContract,
  clearSignerBackendErrors,
  getDraftRoadCareContractAndSaveInCache,
  getMobileContractData,
  saveDraftContract, setHasAgreed,
  setOrUpdateSigner,
  setPaymentMethod,
} from '../../redux/modules/roadCareContract';
import { roadCareContractSignerSchema } from '../../validations/contract/roadCare';
import { getCountries, getStates } from '../../redux/modules/countries';
import { clearCalculation, getPrices } from '../../redux/modules/roadCareCalculation';
import ContractSummary from './ContractSummary';
import useIsAutomaticRecurringPayment from '../../hooks/useIsAutomaticRecurringPayment';
import useSchedule from '../../hooks/useSchedule';
import {
  getCalculatorDiscount,
  getCalculatorTotalPrice,
  getCalculatorTotalPriceAfterDiscount,
} from '../../helpers/price';
import { getContractSummary } from '../../helpers/roadCare/roadCareCalcHelper';
import { bindSummaryContractData } from '../../helpers/roadCare/bindData';
import { ROADCARE_PREFIX } from '../../constants/roadCare';
import { useShowMobileContractLoader } from '../../hooks/useShowMobileContractLoader';
import { getTransportUsageTypes } from '../../redux/modules/vehicles';

import RoadCareCalcHeader from '../../img/roadCareCalcHeader.svg';
import summaryWrapperUseStyles from '../../components/MUI/SummaryWrapper/makeStyles';
import useStepNavigationAmountStyles from '../../components/MUI/StepNavigationAmount/makeStyles';

const RoadCareContractContainer = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const match = useRouteMatch();
  const { search, hash } = useLocation();
  const isDeviceLgSize = useDeviceResize('extraLgSize');
  const { t } = useTranslation();
  const urlSearchParams = new URLSearchParams(search);
  const token = urlSearchParams.get('t');
  const { elementIsFocused } = useSelector((state) => state.global);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const {
    contractData,
    ekengVehicleData,
    paymentMethod,
    orderId,
    draftToken,
    hasAgreed,
    signerBackendErrors,
    loadingDraftContract,
    savingContractInCache,
  } = useSelector((state) => state.roadCareContract, shallowEqual);
  const { loadingMobileContractData } = useSelector((state) => state.roadCareCalculation);
  const summaryWrapperClasses = summaryWrapperUseStyles();
  const stepNavigationAmountClasses = useStepNavigationAmountStyles({ elementIsFocused });
  const { countries, states } = useSelector((state) => state.countriesReducer, shallowEqual);
  const [showMobileContractDataLoader, setShowMobileContractDataLoader] = useState(!!token);
  const [urlParams] = useState(queryString.parse(hash));
  const [currentStep, setCurrentStep] = useState(contractSteps[0]);
  const [unlockedSteps, setUnlockedSteps] = useState(
    urlParams.bfp === 'true' ? getUnlockedStepsUpToPayment(contractSteps) : [contractSteps[0].id],
  );
  const [stepNavigationBox, setStepNavigationBox] = useState({});
  const [openMobileSummary, setOpenMobileSummary] = useState(false);
  useShowMobileContractLoader(loadingMobileContractData, setShowMobileContractDataLoader);

  const { roadCareDetails } = contractData;
  const {
    transportUsageType,
    riskAmount,
    paymentFrequency,
    paymentFrequencies,
    prices,
    isFetchingPrice,
    range,
    isFetchingRiskAmounts,
  } = roadCareDetails;
  const calculatedSchedules = useSchedule(paymentFrequency.value, paymentFrequencies);
  const isAutomaticRecurringPayment = useIsAutomaticRecurringPayment(paymentFrequency);

  useClearAllNotifications();

  const totalPriceAfterDiscount = useMemo(
    () => getCalculatorTotalPriceAfterDiscount(prices, paymentFrequency),
    [prices, paymentFrequency],
  );

  const totalDiscount = useMemo(() => getCalculatorDiscount(prices, paymentFrequency), [
    prices,
    paymentFrequency,
  ]);

  const contractDetails = useMemo(
    () => bindSummaryContractData(t, { start: range.start, end: range.end }),
    [t, range],
  );

  const totalPrice = useMemo(() => getCalculatorTotalPrice(prices, paymentFrequency), [
    prices,
    paymentFrequency,
  ]);

  const contractSummary = useMemo(
    () => getContractSummary(totalDiscount, totalPrice, t, isFetchingPrice),
    [isFetchingPrice, t, totalDiscount, totalPrice],
  );

  useEffect(() => {
    dispatch(getCountries());
    dispatch(getStates());
    dispatch(getTransportUsageTypes(APPLICATION_CONTRACT_TYPES.ROAD_CARE));
  }, [dispatch]);

  useEffect(() => {
    if (riskAmount && !isEmpty(transportUsageType) && !isEmpty(ekengVehicleData)) {
      dispatch(getPrices({
        vehicles: [{
          vehicleDate: Number(ekengVehicleData.released),
          riskAmount: Number(riskAmount),
          vehicleType: transportUsageType.key,
          passport: ekengVehicleData.certNum,
        }],
      }, ACTION_NAME.CONTRACT));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, ekengVehicleData.released, riskAmount, transportUsageType?.key]);

  useEffect(() => {
    if (urlParams.paymentType && totalPrice) {
      dispatch(setPaymentMethod(urlParams.paymentType));
    }
  }, [dispatch, urlParams, totalPrice]);

  useEffect(() => {
    if (urlParams.bfp === 'true' && countries.length && states.length) {
      dispatch(getDraftRoadCareContractAndSaveInCache(urlParams.orderId, urlParams.draftToken))
        .then(() => {
          dispatch(setHasAgreed(true));
          window.location.hash = '';
        })
        .catch(() => {
          setUnlockedSteps([contractSteps[0].id]);
        });
    }
  }, [urlParams, dispatch, countries, states]);

  useEffect(() => () => {
    dispatch(clearCalculation(ACTION_NAME.CONTRACT));
    dispatch(clearRoadCareContract());
  }, [dispatch]);

  const handleSignerActionsBeforeNext = useCallback(
    (data) => {
      handleContractSingerAction(
        dispatch,
        setOrUpdateSigner,
        clearSignerBackendErrors,
        isAuthenticated,
        saveDraftContract,
        data,
      );
    },
    [dispatch, isAuthenticated],
  );

  const handleRoadCareActionsBeforeNext = useCallback(() => {
    if (isAuthenticated) {
      dispatch(saveDraftContract());
    }
  }, [dispatch, isAuthenticated]);

  const beforeNextMapping = {
    signer: handleSignerActionsBeforeNext,
    search: handleRoadCareActionsBeforeNext,
  };

  const goToStep = useCallback(
    (newStep) => goToContractStep(history, match, newStep),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, match.url],
  );

  useEffect(() => {
    if (signerBackendErrors) {
      goToStep(contractSteps[0]);
    }
  }, [signerBackendErrors, goToStep]);

  useEffect(() => {
    if (token) {
      dispatch(getMobileContractData(token, ROADCARE_PREFIX));
    }
  }, [dispatch, token]);

  const handleOpenMobileSummary = useCallback(() => {
    setOpenMobileSummary(!openMobileSummary);
  }, [openMobileSummary]);

  const stepParams = {
    type: APPLICATION_CONTRACT_TYPES.ROAD_CARE,
    signerBackendErrors,
    data: contractData,
    setStepNavigationBox,
    loader: <Loader />,
    showLoader: loadingDraftContract || savingContractInCache,
    isDeviceLgSize,
    isFetchingPrice,
    totalPrice: (
      (isAutomaticRecurringPayment && calculatedSchedules.length)
        ? calculatedSchedules[0].amount : totalPriceAfterDiscount
    ),
    actionName: ACTION_NAME.CONTRACT,
    handleOpenMobileSummary,
    handleActionsBeforeNext: beforeNextMapping[currentStep.id],
    contractSignerSchema: roadCareContractSignerSchema,
    isFetchingRiskAmounts,
    paymentParams: {
      setPaymentMethod,
      paymentMethod,
      orderId,
      draftToken,
    },
    hasAgreed,
  };

  if (showMobileContractDataLoader) {
    return <Loader />;
  }

  return (
    <>
      <EfesContentHeader
        headerText="roadCareContract.title"
        headerImage={RoadCareCalcHeader}
        imageWidth={50}
      />
      <WizardWrapper
        steps={contractSteps}
        stepParams={stepParams}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        unlockedSteps={unlockedSteps}
        setUnlockedSteps={setUnlockedSteps}
        goToStep={goToStep}
        hasWarningModal
        locationsWhitelist={[ROADCARE_CONTRACT_SIGN_PATH]}
        contractSummaryClassName={summaryWrapperClasses.summaryWrapperBox}
        summaryBlock={(
          <ContractSummary
            amount={
              (isAutomaticRecurringPayment && calculatedSchedules.length)
                ? calculatedSchedules[0].amount : totalPriceAfterDiscount
            }
            contractSummary={contractSummary}
            openMobileSummary={openMobileSummary}
            onDismiss={() => setOpenMobileSummary(!openMobileSummary)}
            contractDetails={contractDetails}
            isFetchingPrice={isFetchingPrice}
            paymentFrequency={paymentFrequency}
          />
        )}
      />
      <Grid
        container
        className={cn({
          [stepNavigationAmountClasses.mobileNavigation]: isDeviceLgSize,
        })}
        spacing={isDeviceLgSize ? 4 : 0}
      >
        <Grid
          item
          style={{
            width: isDeviceLgSize ? FULL_WIDTH : EFES_CONTENT_WIDTH,
            paddingRight: !isDeviceLgSize && 20,
          }}
        >
          <Box>{!isEmpty(stepNavigationBox) && stepNavigationBox}</Box>
        </Grid>
      </Grid>
    </>
  );
};

export default RoadCareContractContainer;
